import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = React.useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // No active session — expected on public pages
      setUser(null);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    // Auth cookie is set by the server (httpOnly). No localStorage needed.
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { logError("[auth] logout failed:", err); }
    // Best-effort: clear any legacy localStorage token from the old auth model.
    // localStorage.removeItem can throw in Safari private mode — log if it does.
    try { localStorage.removeItem("tecassist_token"); }
    catch (err) { logError("[auth] legacy token cleanup failed:", err); }
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe }),
    [user, loading, fetchMe],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
